import React from "react"
import { Building2, ShoppingBag, Truck, Globe, Sparkles } from "lucide-react"
import { useTranslation } from "react-i18next"
import { useScrollAnimation } from "../hooks/useScrollAnimation"

export default function TargetAudience() {
    const { t } = useTranslation()
    const ref = useScrollAnimation()

    const audiences = [
        {
            icon: <Building2 className="w-8 h-8 text-emerald-600" />,
            title: t('whoWeServe.audience1_title'),
            description: t('whoWeServe.audience1_desc')
        },
        {
            icon: <ShoppingBag className="w-8 h-8 text-emerald-600" />,
            title: t('whoWeServe.audience2_title'),
            description: t('whoWeServe.audience2_desc')
        },
        {
            icon: <Truck className="w-8 h-8 text-emerald-600" />,
            title: t('whoWeServe.audience3_title'),
            description: t('whoWeServe.audience3_desc')
        }
    ]

    return (
        <section id="who-we-serve" ref={ref} className="scroll-animate py-24 bg-slate-50 overflow-hidden relative border-y border-slate-100">
            <div className="container mx-auto px-4 md:px-6 relative z-10">
                <div className="flex flex-col lg:flex-row gap-16 items-center">
                    <div className="w-full lg:w-1/2">
                        <div className="relative group">
                            <div className="absolute inset-0 bg-emerald-500/20 rounded-3xl blur-2xl group-hover:bg-emerald-500/30 transition-all duration-700"></div>
                            <div className="relative rounded-3xl overflow-hidden shadow-2xl bg-slate-900 border border-white/10 aspect-square sm:aspect-video lg:aspect-square flex items-center justify-center">
                                <div className="absolute inset-0 bg-gradient-to-br from-emerald-600/20 to-blue-900/40"></div>

                                {/* Abstract Global Network Visualization */}
                                <div className="relative w-full h-full p-12 flex items-center justify-center">
                                    <div className="absolute w-64 h-64 border border-emerald-500/20 rounded-full animate-ping opacity-20"></div>
                                    <div className="absolute w-48 h-48 border border-emerald-500/30 rounded-full animate-pulse opacity-40"></div>
                                    <div className="absolute w-32 h-32 border border-emerald-500/50 rounded-full opacity-60"></div>

                                    <Globe className="w-48 h-48 text-emerald-500/20 animate-spin-slow" />

                                    <div className="absolute top-1/4 left-1/4 w-3 h-3 bg-emerald-500 rounded-full shadow-[0_0_15px_rgba(16,185,129,1)]"></div>
                                    <div className="absolute bottom-1/3 right-1/4 w-2 h-2 bg-emerald-400 rounded-full"></div>
                                    <div className="absolute top-1/2 right-1/3 w-2 h-2 bg-blue-400 rounded-full"></div>
                                </div>

                                <div className="absolute bottom-6 left-6 right-6 p-4 bg-white/5 backdrop-blur-md rounded-2xl border border-white/10 flex items-center gap-4">
                                    <div className="w-10 h-10 bg-emerald-500/20 rounded-xl flex items-center justify-center">
                                        <Sparkles className="w-5 h-5 text-emerald-500" />
                                    </div>
                                    <div className="text-white text-sm font-medium">Bousanm Technology Network</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="w-full lg:w-1/2">
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold mb-6 tracking-widest uppercase">
                            Target Markets
                        </div>
                        <h2 className="text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl mb-4 leading-tight">
                            {t('whoWeServe.title')}
                        </h2>
                        <p className="text-xl font-medium text-emerald-600 mb-10">
                            {t('whoWeServe.subtitle')}
                        </p>

                        <div className="space-y-6">
                            {audiences.map((item, index) => (
                                <div key={index} className="card-slide-left flex gap-6 p-6 rounded-2xl bg-white border border-slate-100 shadow-sm hover:shadow-xl hover:translate-x-2 transition-all duration-300 group">
                                    <div className="shrink-0 p-4 bg-emerald-50 rounded-2xl h-fit group-hover:bg-emerald-500 transition-colors duration-300">
                                        {React.cloneElement(item.icon as React.ReactElement<{ className?: string }>, { className: "w-8 h-8 text-emerald-600 group-hover:text-white transition-colors duration-300" })}
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-bold text-slate-900 mb-2">{item.title}</h3>
                                        <p className="text-slate-600 leading-relaxed">{item.description}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}
